import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";

import CustomCheckbox from "@/components/custom-checkbox";
import DownloadApp from "@/components/download-app";
import Header from "@/components/header-v2";
import HeadingWithHighlight from "@/components/heading-with-highlight";
import GlobeIcon from "@/components/icons/globe-icon";
import MarkerIcon from "@/components/icons/marker-icon";
import { linkPropsWithSource } from "@/helpers/url";
import useAuth from "@/hooks/use-auth";
import useNavBar from "@/hooks/use-nav-bar";

import { GameStatusEnum } from "../game/types";
import { QUERY } from "./graphql";
import LocationFilter from "./location-filter";
import LocationItem from "./location-item";
import MapKit from "./map-kit";
import type {
  CompanyType,
  DataType,
  LocationType,
  VariablesType,
} from "./types";

export interface CompanyProps {
  data: {
    company: CompanyType;
  };
}

export default function Company({
  data: { company: serverCompany },
}: CompanyProps) {
  const { isUser, queryContext } = useAuth();
  const [fetchCompany, { data }] = useLazyQuery<DataType, VariablesType>(QUERY);
  useNavBar();

  const company = useMemo(() => {
    if (data?.company.id === serverCompany.id) {
      return data.company;
    }
    return serverCompany;
  }, [serverCompany, data]);

  const hasJustClosedRooms = !company.locations.some((location) =>
    location.games.some((game) => game.status === GameStatusEnum.OP)
  );

  const [filteredLocation, setFilteredLocation] = useState<LocationType | null>(
    null
  );
  const [showClosedRooms, setShowClosedRooms] = useState(hasJustClosedRooms);

  const onCheckShowClosedRooms = () => {
    setShowClosedRooms((prev) => !prev);
  };

  const onHandleFilter = (location: LocationType) => {
    setFilteredLocation(location);
  };

  const onClearFilter = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setFilteredLocation(null);
  };

  useEffect(() => {
    if (isUser) {
      fetchCompany(queryContext({ companySlug: serverCompany.slug, isUser }));
    }
  }, [serverCompany, queryContext, isUser, fetchCompany]);

  const { companyUrl, locations } = useMemo(
    () => ({
      companyUrl: company.url ? linkPropsWithSource(company.url) : undefined,
      locations: company.locations.filter((location) =>
        location.games.some((game) => game.status === GameStatusEnum.OP)
      ),
    }),
    [company]
  );

  return (
    <>
      <Header strategy="fixed" withBackground />
      <MapKit
        className="absolute inset-x-0 top-1 -z-1 md:top-20"
        locations={locations}
      />
      <section className="container mt-1 md:mt-20">
        <div className="flex flex-col justify-center gap-5">
          <HeadingWithHighlight
            as="h1"
            className="pt-17.5 font-semibold text-32/[1.25em] md:pt-0 md:text-60/[1.2em]"
            title={`**${company.name}**`}
          />
          {locations.length > 0 && (
            <div className="flex items-center justify-center gap-2">
              <MarkerIcon />
              <p className="text-20">
                {locations.length} location{locations.length !== 1 && "s"}
              </p>
            </div>
          )}
          {companyUrl && (
            <div className="flex items-center justify-center gap-2 md:flex-row">
              <GlobeIcon />
              <a
                className="text-14"
                rel="noopener noreferrer"
                target="_blank"
                {...companyUrl}
              />
            </div>
          )}
        </div>
        <div className="mt-62.5 md:mt-75">
          <LocationFilter
            company={company}
            filteredLocation={filteredLocation}
            onClearFilter={onClearFilter}
            onHandleFilter={onHandleFilter}
            showClosedRooms={showClosedRooms}
          />
          <CustomCheckbox
            checked={showClosedRooms}
            className="mt-10 justify-center md:mt-0 md:justify-end"
            disabled={hasJustClosedRooms}
            onChange={onCheckShowClosedRooms}
          >
            Show closed experiences
          </CustomCheckbox>
          {company.locations.map((location) => (
            <LocationItem
              company={company}
              filteredLocation={filteredLocation}
              key={location.id}
              location={location}
              showClosedRooms={showClosedRooms}
            />
          ))}
        </div>
        <DownloadApp className="my-20 pt-[167px] md:my-35 md:pt-[183px] md:pb-[183px]" />
      </section>
    </>
  );
}
